import { ApiConstant, KeyConstant, SystemConstant } from "const";
import { all, call, put, select, delay } from "redux-saga/effects";
import { convertString2JSON, getPrefixKey, isJSONString, toSnake } from "utils";
import { LocalDbManagement, getInteractor } from "services/local.service";
import store, {
  CallingActions,
  ConversationActions,
  ConversationSelectors,
  getReduxState,
  GroupInfoActions,
} from "redux-store";
import { StorageUtil } from "utils";
import { remoteApiFactory } from "services";
import { checkCurrentBranchByPrefix, DISPLAY_MSG_TYPES } from "./saga.helper";
import { Mutex } from "async-mutex";
import { isLoginBranch } from "utils/auth.utils";
import { checkCurrentGroup } from "utils/view.utils";
import { updateParentDeleteMessageContent } from "services/message.service";

const HANDLE_QUEUE_LIMIT = 20;
window.delaySyncMsec = 500;
const mutex = new Mutex();
export function* executeQueue() {
  try {
    if (mutex.isLocked()) return;
    window.isQueueRunning = true;
    if (navigator.onLine) {
      // Get all active branches
      const activeBranchList = yield LocalDbManagement.find({ state: SystemConstant.STATE.active });
      // Handling queue on all active branch
      if (activeBranchList.length > 0) {
        let response = yield all(
          activeBranchList.map(item => call(handleQueueTasks, getPrefixKey(item.account_id, item.branch_id))),
        );
        let res = false;
        response.forEach(r => (res = res || r));
        if (!res) {
          return;
        }
      } else {
        return;
      }
    } else {
      return;
    }
  } catch (error) {
    console.error(error);
    return;
  } finally {
    mutex.release();
  }

  // Delay before continue to execute
  yield call(executeQueue);
}

export function* handleQueueTasks(prefixKey) {
  if (false === isLoginBranch() || window.isStopSynchronize) return;

  try {
    const apiList = yield getInteractor(prefixKey).LocalApiCallService.getApiQueue(HANDLE_QUEUE_LIMIT);

    const apiByGroupId = apiList.reduce((apiListByGroup, apiItem) => {
      const groupId = apiItem.group_id || "group_id"; // Using "group_id" for older version

      if (false === Array.isArray(apiListByGroup[groupId])) {
        apiListByGroup[groupId] = [];
      }

      apiListByGroup[groupId].push(apiItem);
      return apiListByGroup;
    }, {});

    if (apiList.length > 0) {
      yield all(Object.keys(apiByGroupId).map(groupId => sendMessageList(prefixKey, apiByGroupId[groupId])));

      // Render view if current group need to be updated
      const selectedGroupId = yield select(ConversationSelectors.getSelectedGroupId);
      if (
        checkCurrentBranchByPrefix(prefixKey) &&
        selectedGroupId &&
        Object.keys(apiByGroupId).includes(selectedGroupId)
      ) {
        yield put(
          ConversationActions.conversationSet({
            isUpdateViewMode: Date.now(),
          }),
        );
      }
    }
    return apiList.length > 0;
  } catch (error) {
    console.error(error);
  }
}

function* sendMessageList(prefixKey, apiList) {
  let sortedList = apiList.sort((a, b) => a.created - b.created);
  for (let index = 0; index < sortedList.length; index++) {
    const item = sortedList[index];
    const { data } = JSON.parse(item.content) || {};
    data["call_id"] = item.id;
    yield sendMessage2Server(prefixKey, data);
  }

  return true;
}

function* sendMessage2Server(prefixKey, data) {
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  try {
    if (data.marks) delete data.marks;
    let response = yield call(remoteApiFactory.getBranchApi(prefixKey).sendMessage, toSnake(data));
    if (response.status === ApiConstant.STT_OK) {
      yield handleQueueSuccess(prefixKey, data);

      if (data.sendType === SystemConstant.SEND_TYPE.leaveGroup && data.removingId === accountId) {
        const payload = {
          groupId: data.groupId,
        };

        if (data.adminId) {
          payload.adminId = data.adminId;
        }

        yield put(
          GroupInfoActions.deleteGroup({
            ...payload,
          }),
        );
      }
      yield getInteractor(prefixKey).LocalApiCallService.poll(data.call_id);
    } else {
      if (response.status === ApiConstant.STT_DUPLICATE_MESSAGE) {
        yield handleQueueSuccess(prefixKey, data);
        yield getInteractor(prefixKey).LocalApiCallService.poll(data.call_id);
      } else if (
        response.status === ApiConstant.STT_UNAUTHORIZED ||
        response.status === ApiConstant.STT_FORBIDDEN ||
        response.status === ApiConstant.STT_MAINTAIN_1 ||
        response.status === ApiConstant.STT_MAINTAIN_2 ||
        response.status === ApiConstant.STT_MAINTAIN_3
      ) {
        if (response.status === ApiConstant.STT_MAINTAIN_1 || response.status === ApiConstant.STT_MAINTAIN_3)
          yield delay(5000);
        // handler refresh token in generic
      } else {
        let apiCall = yield getInteractor(prefixKey).LocalApiCallService.get(data.call_id) || {};
        if (apiCall.retry <= ApiConstant.MAX_RETRY) {
          if (
            response.status === ApiConstant.STT_NOT_FOUND ||
            response.status === ApiConstant.STT_BAD_REQUEST ||
            response.status === ApiConstant.STT_INTERNAL_SERVER
          ) {
            yield getInteractor(prefixKey).LocalApiCallService.save([{ ...apiCall, retry: apiCall.retry + 1 }]);
          }
        } else {
          handleQueueFailed(prefixKey, data);
          yield getInteractor(prefixKey).LocalApiCallService.poll(data.call_id);
        }
      }
    }
  } catch (error) {
    console.error(error);
  }
}

const handleQueueSuccess = async (prefixKey, data) => {
  const localMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
    source_id: data.sourceId,
    send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
    state: SystemConstant.STATE.active,
  });
  if (localMessage && localMessage.id && localMessage.group_id) {
    const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
    const updateLocalMessage = { ...localMessage, modified: Date.now() };
    const updateData = { modified: updateLocalMessage.modified };

    // calculate disappear_time when message is sent successfully
    const messageOptions = updateLocalMessage.options;
    const messageOptionsObj = isJSONString(updateLocalMessage.options)
      ? convertString2JSON(messageOptions, {})
      : messageOptions;
    if (
      localMessage.sender_id === accountId &&
      Boolean(messageOptionsObj.disappearing_f) &&
      Number(messageOptionsObj.time_to_live) > 0
    ) {
      updateData.disappear_time = Number(messageOptionsObj.time_to_live) + updateLocalMessage.modified;
    }

    await getInteractor(prefixKey).LocalMessageService.update(updateData, { id: localMessage.id });

    // Update message content if send delete only me
    if (updateLocalMessage.send_type === SystemConstant.SEND_TYPE.deleteMessageOnlyMe) {
      await getInteractor(prefixKey).LocalMessageService.update(
        { content: "" },
        { source_id: updateLocalMessage.parent_id },
      );

      // Update state = inactive, content = "" for child message
      await getInteractor(prefixKey).LocalMessageService.update(
        { content: "", state: SystemConstant.STATE.inactive },
        {
          parent_id: updateLocalMessage.parent_id,
          send_type: [
            ...SystemConstant.EDITABLE_SEND_TYPE,
            SystemConstant.SEND_TYPE.reaction,
            SystemConstant.SEND_TYPE.deleteReaction,
          ],
        },
      );

      // Update calling message state = inactive if exist
      const callingHistories = await getInteractor(prefixKey).LocalCallHistoryService.find({
        source_id: updateLocalMessage.parent_id,
        state: SystemConstant.STATE.active,
      });
      if (callingHistories.length > 0) {
        const deleteRoomIds = callingHistories.map(item => item.room_id);
        // Dispatch to delete call history
        store.dispatch(
          CallingActions.updateCallHistory(
            {
              state: SystemConstant.STATE.inactive,
              room_ids: deleteRoomIds,
            },
            prefixKey,
          ),
        );
      }
    } else if (updateLocalMessage.send_type === SystemConstant.SEND_TYPE.deleteMessage) {
      await updateParentDeleteMessageContent(prefixKey, updateLocalMessage);
    }

    // Trigger to UI
    if (checkCurrentGroup(localMessage.group_id) && DISPLAY_MSG_TYPES.includes(localMessage.send_type)) {
      store.dispatch(ConversationActions.receivedRemoteMessage(updateLocalMessage));
    }
  }
};

const handleQueueFailed = async (prefixKey, data) => {
  const localMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
    source_id: data.sourceId,
    send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
    state: SystemConstant.STATE.active,
  });
  if (localMessage) {
    const updateLocalMessage = {
      ...localMessage,
      status: SystemConstant.MESSAGE_STATUS.failed,
      modified: Date.now(),
    };
    await getInteractor(prefixKey).LocalMessageService.update(
      {
        modified: updateLocalMessage.modified,
        status: updateLocalMessage.status,
      },
      { id: localMessage.id },
    );

    // Trigger to UI
    const selectedGroupId = getReduxState(ConversationSelectors.getSelectedGroupId);
    if (updateLocalMessage.group_id === selectedGroupId && DISPLAY_MSG_TYPES.includes(updateLocalMessage.send_type)) {
      store.dispatch(ConversationActions.receivedRemoteMessage(updateLocalMessage));
    }
  }
};
